import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ISignageStore } from '../../types/signage';
import { isDefaultStore } from './storeRoute';
import { signageService } from '../../services/signageService';

interface StoreDialogProps {
  open: boolean;
  store: ISignageStore | null; // null = create
  onClose: () => void;
  onSaved: (storeId: string, created: boolean) => void;
  onDelete?: () => void;
}

export const STORE_ID_PATTERN = /^[A-Z0-9_-]{1,20}$/;

const StoreDialog: React.FC<StoreDialogProps> = ({ open, store, onClose, onSaved, onDelete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEdit = !!store;
  const isDefault = !!store && isDefaultStore(store.storeId);

  const [storeId, setStoreId] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStoreId(store?.storeId || '');
      setName(store?.name || '');
      setErrors({});
    }
  }, [open, store]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!isEdit && !STORE_ID_PATTERN.test(storeId.trim().toUpperCase())) {
      next.storeId = 'Use 1-20 letters, numbers, - or _';
    }
    if (!isDefault && !name.trim()) next.name = 'Store name is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const id = storeId.trim().toUpperCase();
      if (isEdit) {
        await signageService.updateStore(store!.storeId, isDefault ? '' : name.trim());
        onSaved(store!.storeId, false);
      } else {
        await signageService.createStore(id, name.trim());
        onSaved(id, true);
      }
      onClose();
    } catch (error: any) {
      const message = typeof error === 'string' ? error : 'Failed to save store';
      setErrors({ [isEdit || !/storeId|exists/i.test(message) ? 'name' : 'storeId']: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isDefault ? 'Default store settings' : isEdit ? 'Edit store' : 'Add store'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!isDefault && (
            <>
          <TextField
            label="Store ID"
            value={storeId}
            onChange={(e) => setStoreId(e.target.value.toUpperCase())}
            error={!!errors.storeId}
            helperText={errors.storeId}
            disabled={isEdit}
            required
            fullWidth
            inputProps={{ maxLength: 20 }}
          />
          <TextField label="Store name" value={name} onChange={(e) => setName(e.target.value)} error={!!errors.name} helperText={errors.name} required fullWidth inputProps={{ maxLength: 100 }} />
            </>
          )}
          <Typography variant="caption" color="text.secondary">
            The Store ID is what staff type at kiosk login, so it must match exactly. It cannot be changed after the store is created. The name is shown in the kiosk header. The entrance and exit devices are set up on the store's page.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isEdit && !isDefault && onDelete && (
          <Button color="error" onClick={onDelete} disabled={loading} sx={{ mr: 'auto' }}>
            Delete store
          </Button>
        )}
        <Button onClick={onClose} disabled={loading} fullWidth={isMobile}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading} fullWidth={isMobile}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreDialog;
